import { useRef, useState } from 'react';
import { clsx } from '../../ds/clsx';
import { Button, Caret, Popover } from '../../ds/components';
import styles from './figures/figures.module.css';

/**
 * Scope menu of the controlling pages („Kennzahlen", „Vertrieb"): the scopes the server offers („Nur ich",
 * „Team …", „Organisation"); hidden when there is only one.
 */

export type ControllingScopeKey = 'me' | 'team' | 'organization';

export function ScopeMenu<K extends ControllingScopeKey>({
  scopes,
  scope,
  onChange,
}: {
  scopes: readonly { key: K; label: string }[];
  scope: K;
  onChange: (scope: K) => void;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  if (scopes.length <= 1) return null;
  const current = scopes.find((item) => item.key === scope)?.label ?? scopes[0]?.label;
  return (
    <>
      <Button
        ref={triggerRef}
        variant="secondary"
        aria-haspopup="listbox"
        aria-expanded={open}
        iconEnd={<Caret size={11} />}
        onClick={() => setOpen((value) => !value)}
      >
        {current}
      </Button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={triggerRef}
        placement="bottom-end"
        offset={6}
        width={240}
        variant="none"
        className={styles.scopeMenu}
        label="Bereich"
        initialFocus="first"
      >
        <div role="listbox" aria-label="Bereich" className={styles.scopeList}>
          {scopes.map((item) => (
            <button
              key={item.key}
              type="button"
              role="option"
              aria-selected={item.key === scope}
              className={clsx(styles.scopeOption, item.key === scope && styles.scopeOptionActive)}
              onClick={() => {
                onChange(item.key);
                setOpen(false);
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      </Popover>
    </>
  );
}
